import React, { memo, useCallback, useMemo } from 'react';

import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import { getAuthorAvatarBorderStyles } from 'app/shared/styles/authorAvatar.styles';
import { newValueStyles, oldValueStyles } from 'app/shared/styles/valueDiff.styles';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';

import { normalizeLoanChangesValue } from '../../../LoanChangesHistory/LoanChangesHistory.utils';
import { AFFECTED_BADGE_CONFIG, ChangeTypeBadgeConfig } from '../../PendingChangesDrawer.constants';
import {
  getAuthorInitials,
  getExactDateTime,
  getRelativeTime,
} from '../../PendingChangesDrawer.utils';
import AiAttributionBadge from '../AiAttributionBadge';

import {
  arrowStyles,
  authorInfoStyles,
  authorNameStyles,
  cardBottomRowStyles,
  cardStyles,
  cardTopRowStyles,
  fieldNameStyles,
  getChangeTypeBadgeDynamicStyles,
  timestampStyles,
  valueDiffRowStyles,
  valueDiffStyles,
} from './PendingChangesCard.styles';
import { getCardDisplayMode } from './PendingChangesCard.utils';

const PendingChangesCard = ({ change, authorColor = null, isAffected = false, onNavigate }) => {
  const {
    propertyName,
    oldValue,
    newValue,
    modifiedOn,
    formattedFullName,
    changeType,
    actorType,
    aiProcessId,
    aiProcessExecutionId,
  } = change;

  const { showAsAdded, effectiveChangeType } = getCardDisplayMode({ changeType, oldValue });

  const changeTypeBadgeConfig =
    ChangeTypeBadgeConfig[effectiveChangeType] ||
    ChangeTypeBadgeConfig[PendingChangesActionType.MODIFIED];
  const badgeConfig = isAffected ? AFFECTED_BADGE_CONFIG : changeTypeBadgeConfig;

  const badgeDynamicStyles = useMemo(
    () => getChangeTypeBadgeDynamicStyles(badgeConfig),
    [badgeConfig],
  );

  const initials = useMemo(() => getAuthorInitials(formattedFullName), [formattedFullName]);

  const avatarBorderStyles = useMemo(() => getAuthorAvatarBorderStyles(authorColor), [authorColor]);

  const formattedOldValue = useMemo(() => normalizeLoanChangesValue(oldValue), [oldValue]);

  const formattedNewValue = useMemo(() => normalizeLoanChangesValue(newValue), [newValue]);

  const relativeTimestamp = useMemo(() => getRelativeTime(modifiedOn), [modifiedOn]);

  const exactTimestamp = useMemo(() => getExactDateTime(modifiedOn), [modifiedOn]);

  const isDeleted = changeType === PendingChangesActionType.DELETED;

  const handleClick = useCallback(() => {
    onNavigate(change);
  }, [onNavigate, change]);

  return (
    <Box sx={cardStyles} onClick={handleClick}>
      <Box sx={cardTopRowStyles}>
        <Typography sx={fieldNameStyles} variant="bold14">
          {propertyName}
        </Typography>
        <Typography component="span" sx={badgeDynamicStyles} variant="bold12">
          {badgeConfig.label}
        </Typography>
      </Box>

      <Box sx={valueDiffStyles}>
        {showAsAdded && (
          <Typography sx={newValueStyles} variant="regular14">
            {!_.isNil(formattedNewValue) ? String(formattedNewValue) : '—'}
          </Typography>
        )}

        {isDeleted && (
          <Typography sx={oldValueStyles} variant="regular14">
            {!_.isNil(formattedOldValue) ? String(formattedOldValue) : '—'}
          </Typography>
        )}

        {!showAsAdded && !isDeleted && (
          <Box sx={valueDiffRowStyles}>
            <Typography sx={oldValueStyles} variant="regular14">
              {!_.isNil(formattedOldValue) ? String(formattedOldValue) : '—'}
            </Typography>
            <Typography sx={arrowStyles} variant="regular14">
              →
            </Typography>
            <Typography sx={newValueStyles} variant="regular14">
              {!_.isNil(formattedNewValue) ? String(formattedNewValue) : '—'}
            </Typography>
          </Box>
        )}
      </Box>

      {!isAffected && (
        <Box sx={cardBottomRowStyles}>
          <Box sx={authorInfoStyles}>
            <Avatar sx={avatarBorderStyles}>{initials}</Avatar>
            <Typography sx={authorNameStyles} variant="regular12">
              {formattedFullName}
            </Typography>
            <AiAttributionBadge
              actorType={actorType}
              aiProcessExecutionId={aiProcessExecutionId}
              aiProcessId={aiProcessId}
            />
          </Box>
          <Tooltip title={exactTimestamp}>
            <Typography sx={timestampStyles} variant="regular12">
              {relativeTimestamp}
            </Typography>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

PendingChangesCard.propTypes = {
  change: PropTypes.shape({
    propertyName: PropTypes.string,
    oldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    newValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    modifiedOn: PropTypes.string,
    formattedFullName: PropTypes.string,
    changeType: PropTypes.string,
    tabName: PropTypes.string,
    id: PropTypes.string,
    actorType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    aiProcessId: PropTypes.string,
    aiProcessExecutionId: PropTypes.string,
  }).isRequired,
  onNavigate: PropTypes.func.isRequired,
  authorColor: PropTypes.string,
  isAffected: PropTypes.bool,
};

export default memo(PendingChangesCard);
